import { useCallback, useEffect, useState } from 'react';

import { ApiKeyClientService } from '../tools/helpers';

interface ScriptState {
  isLoaded: boolean;
  error: Error | null;
  map: typeof google.maps | null;
}

let scriptPromise: Promise<void> | null = null;
let scriptState: ScriptState = {
  isLoaded: false,
  error: null,
  map: null,
};

export const useGoogleMapsScript = () => {
  const [state, setState] = useState<ScriptState>(scriptState);

  const loadScript = useCallback(async () => {
    if (scriptPromise) {
      await scriptPromise;
      setState(scriptState);
      return;
    }

    if (window.google?.maps) {
      scriptState = {
        isLoaded: true,
        error: null,
        map: window.google.maps,
      };
      setState(scriptState);
      return;
    }

    scriptPromise = new Promise<void>(async (resolve, reject) => {
      try {
        const keyData = await ApiKeyClientService.getKeyByKeyName('google_maps');
        if (!keyData?.apiKey) {
          throw new Error('Google Maps API key is undefined');
        }

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${keyData.apiKey}&libraries=places`;

        script.addEventListener('load', () => {
          scriptState = {
            isLoaded: true,
            error: null,
            map: window.google.maps,
          };
          setState(scriptState);
          resolve();
        });

        script.addEventListener('error', (e) => {
          const error = new Error('Failed to load Google Maps script');
          scriptState = {
            isLoaded: false,
            error,
            map: null,
          };
          setState(scriptState);
          reject(error);
        });

        document.head.appendChild(script);
      } catch (error) {
        scriptState = {
          isLoaded: false,
          error: error as Error,
          map: null,
        };
        setState(scriptState);
        reject(error);
      }
    });

    try {
      await scriptPromise;
    } catch (error) {
      console.error(error);
      // Error already handled in the promise
    }
  }, []);

  useEffect(() => {
    loadScript();
  }, [loadScript]);

  return state;
};
